import { XMarkIcon, CalendarDaysIcon } from "@heroicons/react/24/solid";
import CalendarIcon from "components/Icon/CalendarIcon";
import ShadowXIcon from "components/Icon/ShadowXIcon";
import Datepicker from "react-tailwindcss-datepicker";
import { useRef } from "react";
import { t } from "copy/t";
import { GalleryDatepickerContainer, ClearFilterButton } from "./styledComponents";

const DateFilter = ({
  backgroundColor,
  filterSubmit,
  filterReset,
  filters,
}) => {
  const datepickerRef = useRef();

  // Color matching algorithm to find closest datepicker theme color
  function hexToRgb(hex) {
    if (!hex) return null;
    // Normalize: ensure leading #
    const normalized = hex.startsWith('#') ? hex : `#${hex}`;
    // Expand 3-digit shorthand (#abc → #aabbcc)
    if (/^#[a-f\d]{3}$/i.test(normalized)) {
      const r = normalized[1];
      const g = normalized[2];
      const b = normalized[3];
      const result6 = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(`#${r}${r}${g}${g}${b}${b}`);
      if (!result6) return null;
      return { r: parseInt(result6[1], 16), g: parseInt(result6[2], 16), b: parseInt(result6[3], 16) };
    }
    const result = /^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(normalized);
    if (!result) return null;
    return {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
  }

  function colorDistance(color1, color2) {
    const rMean = (color1.r + color2.r) / 2;
    const r = color1.r - color2.r;
    const g = color1.g - color2.g;
    const b = color1.b - color2.b;
    const weightR = 2 + rMean / 256;
    const weightG = 4.0;
    const weightB = 2 + (255 - rMean) / 256;
    return Math.sqrt(weightR * r * r + weightG * g * g + weightB * b * b);
  }

  function closestColor(hexColor, colorList) {
    let closestColorKey = null;
    let minDistance = Infinity;
    const targetColor = hexToRgb(hexColor);
    if (!targetColor) return 'blue'; // fallback if color is unparseable

    for (const colorKey in colorList) {
      const currentColor = hexToRgb(colorList[colorKey]);
      if (!currentColor) continue;
      const distance = colorDistance(targetColor, currentColor);

      if (distance < minDistance) {
        minDistance = distance;
        closestColorKey = colorKey;
      }
    }

    return closestColorKey;
  }

  // These are the theme option colors from the react-tailwindcss-datepicker library
  const colors = {
    blue: "#3b82f6",
    orange: "#f97316",
    yellow: "#eab308",
    red: "#ef4444",
    purple: "#a855f7",
    amber: "#f59e0b",
    lime: "#84cc16",
    green: "#22c55e",
    emerald: "#10b981",
    teal: "#14b8a6",
    cyan: "#06b6d4",
    sky: "#0ea5e9",
    indigo: "#6366f1",
    violet: "#8b5cf6",
    fuchsia: "#d946ef",
    pink: "#ec4899",
    rose: "#f43f5e",
  };

  // picks the closest color the the gallery color for the theme
  const primaryColor = closestColor(backgroundColor, colors);

  const handleSubmit = (data) => {
    if (data === null) {
      filterReset();
      return;
    }
    filterSubmit(data);
  };

  const hasActiveFilter = filters.start !== null || filters.end !== null;

  return (
    <GalleryDatepickerContainer>
      {hasActiveFilter && (
        <ClearFilterButton
          onClick={(e) => {
            e.stopPropagation();
            filterReset();
          }}
          aria-label="Clear date filter"
        >
          <XMarkIcon className="w-3 h-3 text-white" />
        </ClearFilterButton>
      )}
      <Datepicker
        key={filters.start}
        placeholder={t("placeholders.allTime")}
        inputClassName={`bg-transparent text-[12px] font-bold tracking-normal ring-0 focus:ring-0 caret-transparent placeholder:text-white placeholder:uppercase font-['Inter',sans-serif] cursor-pointer p-0 text-white uppercase whitespace-nowrap`}
        containerClassName="flex gap-[6px] items-center flex-shrink-0"
        toggleClassName="w-[20px] h-[20px] flex-shrink-0 order-last"
        toggleIcon={(open) => (
          <CalendarDaysIcon className="w-[20px] h-[20px] text-white" />
        )}
        value={{ startDate: filters.start, endDate: filters.end }}
        displayFormat="MM/DD/YYYY"
        onChange={handleSubmit}
        primaryColor={primaryColor}
        showShortcuts={true}
        asSingle={false}
        startFrom={filters.start ? new Date(filters.start) : undefined}
      />
    </GalleryDatepickerContainer>
  );
};

export default DateFilter;
